import * as React from "react";
import { cn } from "@/lib/utils";

const headingVariants = {
  display:
    "text-display font-light lg:font-semibold tracking-tight text-gray-900 dark:text-white",
  h1: "text-h1 font-semibold tracking-tight text-gray-900 dark:text-white",
  h2: "text-h2 font-medium text-gray-900 dark:text-white",
  h3: "text-h3 font-medium text-gray-900 dark:text-white",
} as const;

type HeadingVariant = keyof typeof headingVariants;

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement> & {
  variant?: HeadingVariant;
  as?: keyof React.JSX.IntrinsicElements;
  muted?: boolean;
};

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, variant = "h1", as, muted, ...props }, ref) => {
    const Comp: any = as ?? (variant === "display" ? "h1" : variant);
    return (
      <Comp
        ref={ref}
        className={cn(
          headingVariants[variant],
          muted && "text-muted-foreground",
          className,
        )}
        {...props}
      />
    );
  },
);
Heading.displayName = "Heading";

const textVariants = {
  body: "text-body",
  small: "text-small",
} as const;

type TextVariant = keyof typeof textVariants;

type TextProps = React.HTMLAttributes<HTMLParagraphElement> & {
  variant?: TextVariant;
  as?: keyof React.JSX.IntrinsicElements;
  muted?: boolean;
};

export const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  ({ className, variant = "body", as, muted, ...props }, ref) => {
    const Comp: any = as ?? (variant === "small" ? "small" : "p");
    return (
      <Comp
        ref={ref}
        className={cn(
          muted && "text-muted-foreground",
          textVariants[variant],
          className,
        )}
        {...props}
      />
    );
  },
);
Text.displayName = "Text";
